/* oxlint-disable no-inner-declarations */
import * as rq from '@tanstack/react-query';

import { useDexFetchClient } from '@/api/looker/utils/use-dex-fetch-client';
import type { BatteryLevel } from '@/data-explorer-v2/entities/battery-level';
import type { Node } from '@/data-explorer-v2/entities/node';
import { useMe } from '@/hooks/use-me';

export namespace ListIds {
  export const key = (level: BatteryLevel, parentId: string) => [
    'data-explorer',
    'list-ids',
    level,
    parentId,
  ];

  export type QueryParams = {
    retry?: number | boolean;
    level: BatteryLevel;
    parentId: string | undefined;
  };

  export type Output = { ids: Node[] };

  type FetchResult = Record<string, Record<string, string>>;

  function outputFromBody(data: FetchResult, level: BatteryLevel): Output {
    const ids: Node[] = [];

    for (const [key, innerObj] of Object.entries(data)) {
      for (const [innerKey, id] of Object.entries(innerObj)) {
        ids.push({
          id,
          label: innerKey,
          source: key,
          level,
        });
      }
    }

    return { ids };
  }

  export function useQuery({ retry, level, parentId }: QueryParams) {
    const fetchClient = useDexFetchClient();
    const me = useMe();

    return rq.useQuery<Output>({
      retry,
      enabled: Boolean(parentId),
      queryKey: key(level, parentId ?? ''),
      async queryFn({ signal }) {
        const data = await fetchClient<FetchResult>({
          path: `/v2/ids/${me.tenant}`,
          params: {
            ...(parentId && level !== 'system' && { 'parent-id': parentId }),
            level,
          },
          signal,
        });

        return outputFromBody(data, level);
      },
    });
  }
}
